import React from 'react';
import { get } from 'lodash-es';

import { emptyObject } from 'ui-components/default-props/empty';
import { BulkActions } from '../../../../enums/bulk-actions';
import { PackageWithDimensions } from './package-with-dimensions';
import Global from '../../../../store/global-selectors';
import { STANDARD_PACKAGE_ID } from '../../../../services/package-service';
import { getCachedNormalizedBYOLProviders } from '../../../../hooks/use-normalized-BYOL-providers';

const isCustomPackage = ({ isCustom }) => !!isCustom;

const filterByServices =
  (carrierIds, selectedServiceId) =>
  (pkg, state = {}) => {
    const uniqueCarriers = new Set(carrierIds);
    const multipleCarriersSelected = uniqueCarriers.size > 1;
    const showGenericPackagesOnly = !selectedServiceId && multipleCarriersSelected;
    const packageIsCustom = isCustomPackage(pkg);
    if (showGenericPackagesOnly) {
      return packageIsCustom || (pkg.packageTypeId === STANDARD_PACKAGE_ID && carrierIds.some((c) => c === pkg.carrierId));
    }
    if (selectedServiceId) {
      const { carrierId: selectedServiceCarrierId } = Global.selectSellerServiceById(state, { serviceId: selectedServiceId });
      return packageIsCustom || pkg.carrierId === selectedServiceCarrierId;
    }
    return packageIsCustom || !carrierIds || carrierIds.length === 0 || carrierIds.some((c) => c === pkg.carrierId);
  };

const doesPackageRequireDimensions = (selectedPackage) => {
  const { packageTypeId = 0 } = selectedPackage;
  const isDefaultPackage = packageTypeId.toString() === STANDARD_PACKAGE_ID;
  return isDefaultPackage || isCustomPackage(selectedPackage);
};

const isBYOLPackage = (p) => {
  const BYOLProviders = getCachedNormalizedBYOLProviders();
  return !BYOLProviders[p.carrierId];
};

export const Package = {
  id: BulkActions.packageBulkAction.name,

  render: (onChange, model, jobData, isEmpty) => {
    const shippingConfiguration = get(model, 'shippingConfiguration', emptyObject);
    const selectedServiceId = shippingConfiguration.serviceId;
    const { services } = jobData;
    const packageFilters = [
      isBYOLPackage,
      filterByServices(
        services.map((srv) => srv.carrierId),
        selectedServiceId,
      ),
    ];
    const firstPackage = get(shippingConfiguration, 'packages.0', emptyObject);
    const selectedPackageIds = [shippingConfiguration?.packageSetId || firstPackage.packageId];
    const shouldRenderDimensions = shippingConfiguration?.packageSetId ? false : doesPackageRequireDimensions(firstPackage);

    const updateShippingConfiguration = (newShippingConfig) => {
      onChange({
        ...model,
        shippingConfiguration: {
          ...shippingConfiguration,
          ...newShippingConfig,
        },
      });
    };

    const handlePackageSetSelection = (packageSet) => {
      updateShippingConfiguration({
        packageSetId: packageSet.packageSetId,
        packages: packageSet.packages,
        packageSetName: packageSet.name,
      });
    };

    const handlePackageSelection = (selectedPackage) => {
      updateShippingConfiguration({
        // unset packageSetId if user previously selected a package set
        packageSetId: null,
        packages: [
          {
            ...firstPackage,
            ...selectedPackage,
          },
        ],
      });
    };

    const updateSetPackage = (selectedPackage) => {
      if (!selectedPackage) {
        return;
      }

      const isPackageSet = !!selectedPackage.packageSetId;
      return isPackageSet ? handlePackageSetSelection(selectedPackage) : handlePackageSelection(selectedPackage);
    };

    const updateDimensions = (dimensions) => {
      updateShippingConfiguration({
        packages: [
          {
            ...firstPackage,
            dimensions,
          },
        ],
      });
    };

    return (
      <PackageWithDimensions
        dimensions={firstPackage.dimensions}
        hasError={isEmpty}
        onPackageSelected={updateSetPackage}
        onSetDimensions={updateDimensions}
        packageFilters={packageFilters}
        selectedPackageIds={selectedPackageIds}
        shouldRenderDimensions={shouldRenderDimensions}
      />
    );
  },
};
